import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import languageConfig from '../../config/languages';
import { getEnabledNavigationItems } from '../../config/features';
import { trackNavigation, trackLanguageChange } from '../../utils/googleAnalytics';
import { trackCTAWithAttribution } from '../../utils/utmTracking';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  MenuItem,
  Select,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

const colors = {
  black: '#000000',
  darkRed: '#451a15',
  red: '#970000',
  yellow: '#fdb200',
  offWhite: '#f6ede7',
};

function NavigationBar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const changeLanguage = (event) => {
    const newLang = event.target.value;
    i18n.changeLanguage(newLang);
    trackLanguageChange(newLang);
  };

  // Navigation items controlled by feature toggles in src/config/features.js
  const navigationItems = getEnabledNavigationItems();

  const handleNavigation = (section) => {
    trackNavigation(section);
    setMobileMenuOpen(false); // Close menu immediately for better UX

    // Use standard hash anchors for navigation
    if (location.pathname === '/') {
      // On home page, use hash navigation
      if (location.hash === `#${section}`) {
        // Already at this section, just scroll with offset
        const element = document.getElementById(section);
        if (element) {
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          // Special handling for 'try' section which needs more offset
          const navbarHeight = section === 'try' ? 150 : 100;
          const offsetPosition = elementPosition - navbarHeight;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      } else {
        navigate(`#${section}`);
      }
    } else {
      // On other pages, navigate home with hash
      navigate(`/#${section}`);
    }
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: colors.offWhite,
        boxShadow: 'none',
        borderBottom: `2px solid ${colors.red}`,
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 64, sm: 70 },
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Logo */}
        <Box
          onClick={() => {
            trackNavigation('home');
            // Navigate to home and scroll to top
            if (location.pathname === '/' && !location.hash) {
              // Already on home, just scroll to top
              window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
              navigate('/');
            }
          }}
          sx={{
            cursor: 'pointer',
            mr: { xs: 'auto', md: 4 },
            flexShrink: 0,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: 'TanAshford, serif',
              fontWeight: 'bold',
              fontSize: { xs: '1.5rem', sm: '1.8rem' },
              color: colors.darkRed,
              whiteSpace: 'nowrap',
            }}
          >
            Ulpian
          </Typography>
        </Box>

        {/* Navigation Items */}
        <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
          {navigationItems.map((item) => (
            <Button
              key={item}
              onClick={() => (item === 'coverage' || item === 'security' || item === 'news') ? navigate(`/${item}`) : handleNavigation(item)}
              variant={item === 'try' ? 'contained' : 'text'}
              sx={{
                ...(item === 'try' ? {
                  backgroundColor: colors.red,
                  color: colors.yellow,
                  fontWeight: 'bold',
                  mx: 1,
                  px: 2.5,
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  '&:hover': {
                    backgroundColor: colors.darkRed,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                  },
                  transition: 'all 0.3s ease',
                } : {
                  color: colors.darkRed,
                  mx: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(151, 0, 0, 0.08)',
                  },
                }),
              }}
            >
              {t(`nav.${item}`)}
            </Button>
          ))}
        </Box>

        {/* Right side items */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: colors.red,
              color: colors.yellow,
              ml: { xs: 1, sm: 2 },
              borderRadius: '8px',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 0.75 },
              '&:hover': { backgroundColor: colors.darkRed },
            }}
            href="https://app.ulpian.com"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCTAWithAttribution('open_app', 'navigation_bar')}
          >
            {t('nav.open_app')}
          </Button>

          <Select
            value={i18n.language}
            onChange={changeLanguage}
            sx={{
              ml: 2,
              color: colors.darkRed,
              display: { xs: 'none', md: 'block' },
              '.MuiOutlinedInput-notchedOutline': { borderColor: colors.darkRed },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.red,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.red,
              },
              '.MuiSvgIcon-root': { color: colors.darkRed },
            }}
          >
            {languageConfig.getEnabledLanguages().map((lang) => (
              <MenuItem key={lang.code} value={lang.code}>
                {lang.name}
              </MenuItem>
            ))}
          </Select>

          {/* Mobile menu icon */}
          <IconButton
            sx={{
              ml: 1,
              display: { xs: 'flex', md: 'none' },
              flexShrink: 0,
            }}
            aria-label="Open mobile navigation menu"
            onClick={handleMobileMenuToggle}
          >
            <MenuIcon sx={{ color: colors.darkRed }} />
          </IconButton>
        </Box>
      </Toolbar>

      {/* Mobile Menu Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 280,
            backgroundColor: colors.offWhite,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'TanAshford, serif',
                fontWeight: 'bold',
                fontSize: '1.8rem',
                color: colors.darkRed,
              }}
            >
              Ulpian
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon sx={{ color: colors.darkRed }} />
            </IconButton>
          </Box>

          <Divider sx={{ backgroundColor: colors.red, mb: 2 }} />

          <List>
            {navigationItems.map((item) => (
              <ListItem key={item} disablePadding>
                <ListItemButton
                  onClick={() => {
                    if (item === 'coverage' || item === 'security' || item === 'news') {
                      navigate(`/${item}`);
                      setMobileMenuOpen(false);
                    } else {
                      handleNavigation(item);
                    }
                  }}
                  sx={{
                    backgroundColor: item === 'try' ? colors.red : 'transparent',
                    mx: item === 'try' ? 2 : 0,
                    borderRadius: item === 'try' ? '8px' : 0,
                    mb: item === 'try' ? 1 : 0,
                    '&:hover': {
                      backgroundColor: item === 'try' ? colors.darkRed : 'rgba(151, 0, 0, 0.08)',
                    },
                  }}
                >
                  <ListItemText
                    primary={t(`nav.${item}`)}
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: item === 'try' ? colors.yellow : colors.darkRed,
                        fontWeight: item === 'try' ? 'bold' : 500,
                        textAlign: item === 'try' ? 'center' : 'left',
                        textTransform: item === 'try' ? 'uppercase' : 'none',
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Divider sx={{ backgroundColor: colors.red, my: 2 }} />

          <Box sx={{ px: 2 }}>
            <Select
              value={i18n.language}
              onChange={changeLanguage}
              fullWidth
              size="small"
              sx={{
                color: colors.darkRed,
                backgroundColor: colors.offWhite,
                '.MuiOutlinedInput-notchedOutline': { borderColor: colors.darkRed },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.red,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: colors.red,
                },
                '.MuiSvgIcon-root': { color: colors.darkRed },
              }}
            >
              {languageConfig.getEnabledLanguages().map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
      </Drawer>
    </AppBar>
  );
}

export default NavigationBar;
